/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/DecisionCircles.tsx
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-CIRCLES-001
 * PURPOSE: Render the ONE 24px filled confirm circle + 24px ghost reject
 *   circle on fine pointers; both grow to a REAL 44px box on coarse pointers.
 * ROLE IN ARCHITECTURE: Presentational control leaf under DecisionActions.
 * MAIN EXPORTS: DecisionCircles.
 * NON-RESPONSIBILITIES: No transports, no queue logic — callbacks only.
 * NOTES FOR FUTURE AI: Circles are 24px with 14px icons on fine pointers
 *   (plan "Premium visual language"). Spinners appear only in the runner's
 *   deferred spinner phase — never add an immediate spinner (sub-400ms must
 *   feel instant).
 *   change #269 C1: an earlier generation grew the coarse-pointer hit area
 *   with a transparent `before:` pseudo-element instead of the real box,
 *   because the 24px circles sat inside the fixed 40px DECISION_H band. A
 *   pseudo-element can never change what getBoundingClientRect() reports,
 *   so the coarse e2e "44px touch-target" spec (rightly) failed. Fixed by
 *   growing the circles to a real `pointer-coarse:h-11 w-11` (44px) AND
 *   letting DecisionLine.tsx's band grow to DECISION_H_COARSE_MIN on coarse
 *   pointers only — fine-pointer geometry (DECISION_H, these 24px circles)
 *   is byte-for-byte unchanged because `pointer-coarse:` variants are inert
 *   under `pointer: fine`.
 */
import { Check, Loader2, X } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { DecisionHue } from './hues'

export interface DecisionCirclesProps {
  hue: DecisionHue
  confirmLabel: string
  rejectLabel: string
  /** Omit to render the reject circle alone (picker-primary decisions). */
  onConfirm?: () => void
  onReject: () => void
  /** Which side is in the runner's deferred spinner phase, if any. */
  busySide?: 'confirm' | 'reject' | null
  disabled?: boolean
}

export function DecisionCircles({
  hue,
  confirmLabel,
  rejectLabel,
  onConfirm,
  onReject,
  busySide = null,
  disabled = false,
}: DecisionCirclesProps) {
  const base =
    'relative inline-flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 ' +
    'active:scale-[.97] motion-reduce:transform-none pointer-coarse:h-11 pointer-coarse:w-11'
  return (
    <>
      {onConfirm && (
        <button
          type="button"
          onClick={onConfirm}
          disabled={disabled}
          title={confirmLabel}
          aria-label={confirmLabel}
          data-testid="reader-decision-accept"
          className={cn(base, hue.fill, disabled && 'cursor-not-allowed opacity-60')}
        >
          {busySide === 'confirm' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      )}
      <button
        type="button"
        onClick={onReject}
        disabled={disabled}
        title={rejectLabel}
        aria-label={rejectLabel}
        data-testid="reader-decision-reject"
        className={cn(
          base,
          'border-border bg-card text-muted-foreground hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        {busySide === 'reject' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <X className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
    </>
  )
}
